/**
 * @fileoverview Pure derivations + formatting for PipelineJobPanel (MOR-882)
 *
 * Split out of `pipeline-job-panel.tsx` so the panel's row/response/history
 * derivations and the vote-history/withdrawal/reminder copy are unit
 * testable without mounting the (very large) panel component. Mixing
 * component and non-component exports would also break React Fast Refresh.
 */

import { format } from "date-fns";
import type { FunctionReturnType } from "convex/server";
import { api } from "@cvx/_generated/api";
import type {
  ContactRole,
  JobContactRow,
} from "@/components/jobs/phases/contacts/types";

export type ConflictCheck = FunctionReturnType<
  typeof api.preAppointment.conflicts.listForJob
>[number];
export type ConflictCheckResponse = ConflictCheck["responses"][number];
export type ConflictVoteHistoryEntry =
  ConflictCheckResponse["voteHistory"][number];

/**
 * The only roles listed in the panel's Key Contacts section — the full
 * contact set (every creditor and third party) stays one click away on the
 * job's Contacts page instead.
 */
export const DIRECTOR_ROLES: ReadonlySet<ContactRole> = new Set([
  "director",
  "former_director",
]);

/**
 * The job's directors for the Key Contacts section, current before former,
 * then alphabetical. Rows come from the reconcile pipeline, so a pitch with
 * no processed documents has none yet.
 */
export function deriveKeyContactRows(
  contacts: Array<JobContactRow> | undefined,
): Array<JobContactRow> {
  const all = contacts ?? [];
  return all
    .filter((contact) =>
      (contact.roles ?? []).some((role) => DIRECTOR_ROLES.has(role)),
    )
    .sort((a, b) => {
      const aCurrent = (a.roles ?? []).includes("director");
      const bCurrent = (b.roles ?? []).includes("director");
      if (aCurrent !== bCurrent) return aCurrent ? -1 : 1;
      return a.displayName.localeCompare(b.displayName);
    });
}

/** Open vs. closed conflict checks — one open check per job is enforced server-side. */
export function splitConflictChecksByStatus(
  checks: Array<ConflictCheck> | undefined,
): { openChecks: Array<ConflictCheck>; closedChecks: Array<ConflictCheck> } {
  const all = checks ?? [];
  return {
    openChecks: all.filter((c) => c.status === "open"),
    closedChecks: all.filter((c) => c.status === "closed"),
  };
}

/** The current user's own response row on a check, if they were polled. */
export function findMyResponse(
  check: ConflictCheck,
): ConflictCheckResponse | undefined {
  return check.responses.find(
    (r) => check.myResponseId !== null && r._id === check.myResponseId,
  );
}

/** Responses where a vote has actually been cast. */
export function votedResponses(
  check: ConflictCheck,
): Array<ConflictCheckResponse> {
  return check.responses.filter((r) => r.vote !== undefined);
}

/** Responses to render given the "hide no response" toggle. */
export function visibleConflictResponses(
  check: ConflictCheck,
  hideNoResponse: boolean,
): Array<ConflictCheckResponse> {
  return hideNoResponse ? votedResponses(check) : check.responses;
}

/** "conflict flagged" / "no conflict" — the plain-language name for a vote. */
export function voteLabel(vote: "yes" | "no"): string {
  return vote === "yes" ? "conflict flagged" : "no conflict";
}

/**
 * One line of a member's vote-history trail (MOR-965), exactly as rendered:
 * the action, an optional quoted reason, the timestamp, and an email-link
 * marker.
 */
export function formatVoteHistoryLine(entry: ConflictVoteHistoryEntry): string {
  const action =
    entry.action === "withdrawn"
      ? "Withdrew their vote"
      : entry.vote === "yes"
        ? "Declared a conflict"
        : "Voted no conflict";
  const reason =
    entry.action === "voted" && entry.reason ? ` — “${entry.reason}”` : "";
  const timestamp = format(entry.at, "d MMM yyyy, h:mmaaa");
  const via = entry.via === "link" ? " · via email link" : "";
  return `${action}${reason} · ${timestamp}${via}`;
}

/**
 * "16 Mar" if this response's most recent history event was a withdrawal
 * and no vote is currently on record, else null.
 */
export function describeWithdrawal(
  response: ConflictCheckResponse,
): string | null {
  const lastEvent = response.voteHistory.at(-1);
  if (response.vote !== undefined || lastEvent?.action !== "withdrawn") {
    return null;
  }
  return format(lastEvent.at, "d MMM");
}

/** "16 Mar" if this (still-unvoted) member has been sent a manual reminder. */
export function describeReminder(
  response: ConflictCheckResponse,
): string | null {
  if (response.vote !== undefined || response.lastRemindedAt === undefined) {
    return null;
  }
  return format(response.lastRemindedAt, "d MMM");
}

/** Toast copy for a bulk resend to everyone still pending. */
export function formatResendToastMessage(count: number): string {
  return `Vote link resent to ${count} member${count === 1 ? "" : "s"} who ${
    count === 1 ? "hasn't" : "haven't"
  } voted.`;
}

/** The closed-checks disclosure toggle's label. */
export function formatClosedToggleLabel(
  count: number,
  showClosed: boolean,
): string {
  return `${showClosed ? "Hide" : "Show"} ${count} closed check${count === 1 ? "" : "s"}`;
}
